import { useDispatch } from "react-redux";
import Link from "next/link";
import { useRouter } from "next/router";
import clsx from "clsx";
import { parse, format } from "date-fns";

import {
  hideMyMenuSelectWeekModal,
  useOrdersQuery,
} from "@chef/state-management";
import { useIntercom } from "@chef/hooks";
import {
  useAppDispatch,
  useAppSelector,
  useCalendarData,
} from "@chef/state-management/hooks";
import {
  formatDate,
  getNextCutoff,
  isMealboxBasketType,
  orderHasBeenDelivered,
  shouldShowDelivery,
} from "@chef/helpers";
import { Card } from "@chef/components";
import {
  BRAND_PATHS,
  language,
  ORDER_STATUS_IDS,
  ORDER_TYPE_IDS,
} from "@chef/constants";
import { isEqualStrings } from "@chef/utils/equal";

import { Modal } from "./modal/Modal";

const intl = (
  {
    no: {
      WEEK: "Uke",
      SHORT_DATE_FORMAT: "d. MMM",
      CANCELLED: "Avbestilt",
      CLOSED: "Stengt",
      DELIVERED: "Levert",
      IN_TRANSIT: "Underveis",
    },
    se: {
      WEEK: "Vecka",
      SHORT_DATE_FORMAT: "d MMM",
      CANCELLED: "Avbokad",
      CLOSED: "Stängt",
      DELIVERED: "Levererad",
      IN_TRANSIT: "På väg",
    },
    dk: {
      WEEK: "Uge",
      SHORT_DATE_FORMAT: "d. MMM",
      CANCELLED: "Afbestilt",
      CLOSED: "Lukket",
      DELIVERED: "Leveret",
      IN_TRANSIT: "Undervejs",
    },
  } as const
)[language];

const parseDeliveryTime = (time?: string) => {
  const parsedTime = parse(time ?? "", "HH:mm:ss", new Date());
  return format(parsedTime, "HH:mm");
};

type WeekInformationProps = {
  href: string;
  week: number;
  isActive: boolean;
  message: string;
  id: string;
};

const WeekInformation = ({
  href,
  week,
  isActive,
  message,
  id,
}: WeekInformationProps) => {
  const dispatch = useAppDispatch();

  return (
    <Link
      id={id}
      href={href}
      onClick={() => dispatch(hideMyMenuSelectWeekModal())}
      passHref
      key={week}
      className={clsx(
        "flex justify-between rounded-lg p-2 align-middle hover:bg-primary group",
        isActive && "bg-primary text-white font-bold",
      )}
    >
      <span className="text-sm/5 group-hover:text-white">
        {intl.WEEK} {week}
      </span>
      <span
        className={clsx(
          "text-sm/5 group-hover:text-white",
          !isActive && "text-grey-1",
        )}
      >
        {message}
      </span>
    </Link>
  );
};

export const MyMenuSelectWeekModal = () => {
  const dispatch = useDispatch();
  const { pathname, query } = useRouter();

  const { open } = useAppSelector((state) => state.modal.myMenuSelectWeek);

  const { week, year } = getNextCutoff();

  const { calendar, isLoading: isLoadingCalendar } = useCalendarData({
    week,
    year,
  });

  const { order, isLoading: isLoadingOrder } = useOrdersQuery(
    {
      count: 1,
      orderTypes: [ORDER_TYPE_IDS.RECURRING],
      orderStatuses: [ORDER_STATUS_IDS.PROCESSING, ORDER_STATUS_IDS.FINISHED],
    },
    {
      selectFromResult: ({ data, isLoading }) => ({
        order:
          data?.orders[0] && shouldShowDelivery(data.orders[0].deliveryDate)
            ? {
                week: data.orders[0].week,
                hasBeenDelivered: orderHasBeenDelivered(data.orders[0]),
              }
            : undefined,
        isLoading,
      }),
    },
  );

  useIntercom(!open || isLoadingCalendar || isLoadingOrder);

  return (
    <Modal
      name="my-menu-select-week-modal"
      open={open}
      onClose={() => {
        dispatch(hideMyMenuSelectWeekModal());
      }}
      animation="fly-up"
      gesture="pull-down-to-close"
      hideCloseButton
    >
      <Card
        noPadding
        className="flex flex-col w-4/5 p-2 pt-4 m-auto mt-10 space-y-2 lg:pt-2 rounded-3xl md:w-2/5"
      >
        <>
          {order && (
            <WeekInformation
              href={BRAND_PATHS.MY_DELIVERY}
              isActive={isEqualStrings(pathname, BRAND_PATHS.MY_DELIVERY)}
              week={order.week}
              message={
                order.hasBeenDelivered ? intl.DELIVERED : intl.IN_TRANSIT
              }
              id={`my-menu-select-week-modal-week-${order.week}`}
            />
          )}
          {calendar
            ?.filter(Boolean)
            .slice(0, 3)
            .map((calendarWeek) => {
              const href = BRAND_PATHS.MY_MENU_WEEK_HREF({
                week: calendarWeek.week,
                year: calendarWeek.year,
              });
              const isActive =
                Number(query["week"]) === calendarWeek.week &&
                Number(query["year"]) === calendarWeek.year;

              const shippingDetails = calendarWeek.baskets.find(
                (b) =>
                  b.week === calendarWeek.week &&
                  b.year === calendarWeek.year &&
                  isMealboxBasketType(b),
              )?.shippingDetails;

              const deliveryInformation =
                shippingDetails?.plannedDeliveryInformation;

              const isPaused = !shippingDetails?.hasDelivery;
              const isUndeliverable =
                !deliveryInformation?.isAvailableForDelivery;

              let deliveryMessage = "";

              if (isUndeliverable) {
                deliveryMessage = intl.CLOSED;
              } else if (isPaused) {
                deliveryMessage = intl.CANCELLED;
              } else if (
                deliveryInformation?.deliveryDate &&
                deliveryInformation?.from &&
                deliveryInformation?.to
              ) {
                const deliveryDate = new Date(deliveryInformation.deliveryDate);
                const deliveryTimeFrom = parseDeliveryTime(
                  deliveryInformation.from,
                );
                const deliveryTimeTo = parseDeliveryTime(
                  deliveryInformation.to,
                );

                const deliveryDateMessage = formatDate(
                  deliveryDate,
                  intl.SHORT_DATE_FORMAT,
                );

                deliveryMessage = `${deliveryDateMessage} ${deliveryTimeFrom} - ${deliveryTimeTo}`;
              }

              return (
                <WeekInformation
                  href={href}
                  key={week}
                  isActive={isActive}
                  week={calendarWeek.week}
                  message={deliveryMessage}
                  id={`my-menu-select-week-modal-week-${calendarWeek.week}`}
                />
              );
            })}
        </>
      </Card>
    </Modal>
  );
};
